import { Injectable } from "@angular/core";
import { EMPTY, Observable, of } from "rxjs";
import { map, shareReplay, switchMap } from "rxjs/operators";
import { RepositoryService } from "./repository.service";
import { Risk } from "../../_interfaces/risk.model";
import { RiskCategory, RiskConfig } from "src/app/_interfaces/RiskConfig.model";
import { HeadlineRisk } from "src/app/_interfaces/HeadlineRisk.model";
import { RiskUser } from "src/app/_interfaces/RiskUser.model";
import { RiskType, RiskTypeEnum } from "../../_interfaces/RiskType.model";
import { RiskCacheService } from "./risk-cache.service";
import { RiskAudit } from "src/app/_interfaces/riskAudit.model";
import { RiskSummary } from "../../_interfaces/RiskSummary.model";

@Injectable()
export class RiskService {
  //all headline risks
  public headlineRisks$: Observable<HeadlineRisk[]> = this.getRiskConfig()
    .pipe(
      map(riskCategories => riskCategories.headlineRisks)
    );

  //all risk types
  public riskTypes$: Observable<RiskType[]> = this.getRiskConfig()
    .pipe(
      map(riskCategories => riskCategories.riskTypes)
  );


  constructor(
    private repository: RepositoryService,
    private riskCahceService: RiskCacheService
  ){}

  //get risks based on api address
  public getRisks(apiAddress: string): Observable<Risk[]> {
    return this.repository.getData(apiAddress)
      .pipe(
        map(response => response as Risk[])
      );
  }

  //get all risks for a category
  public getRisksForCategory(category: RiskType): Observable<Risk[]> {
    let apiAddress = 'Risks';

    //build api endpoint path
    if (category.id === RiskTypeEnum.all && category.name) {
      apiAddress += `/${category.name}`
    } else if (category.id !== RiskTypeEnum.all) {
      apiAddress += `?RiskType=${category.id}`;
    }
    return this.getRisks(apiAddress);
  }

  //get all risks from risk type and headline risk
  public getRisksForHeadlineRisks(riskType: RiskType, headlineRiskType: number): Observable<Risk[]> {
    return this.getRisksForCategory(riskType).pipe(
      map(risks => {
        //filter risks on risk type and headlineRisks
        switch (riskType.id) {
          case RiskTypeEnum.strategic:
            return risks.filter(risk => risk.strategicRiskType == headlineRiskType);

          case RiskTypeEnum.operational:
            return risks.filter(risk => risk.operationalRiskType == headlineRiskType);

          case RiskTypeEnum.financial:
            return risks.filter(risk => risk.financialRiskType == headlineRiskType);

          default:
            return risks;
        }
      }));
  }

  //get a risk by id
  public getRisk(id: number): Observable<Risk> {
    let apiAddress = `Risks/${id}`;

    return this.repository.getData(apiAddress).pipe(
        map(res => res as Risk)
      );
  }

  //filter headline risks by riskType
  public filterHeadlineRisks(riskType: number): Observable<HeadlineRisk[]> {
    return this.headlineRisks$.pipe(
      map((headlineRisks) =>
        headlineRisks.filter(headlineRisk => headlineRisk.riskType === riskType)
      )
    );
  }

  //find a risk type
  public getRiskTypeDetails(id: number): Observable<RiskType> {
    return this.riskTypes$
      .pipe(
        switchMap((riskTypes) => {
          let riskType = riskTypes.find(riskType => riskType.id === id);
          return riskType ? of(riskType) : EMPTY;
        })
      )
  }

  //find and return a headline risk
  public getHeadlineRiskDetails(risk: Risk | RiskAudit | RiskSummary): Observable<HeadlineRisk> {
    let headlineRiskId = 0;
    switch (risk.riskType) {
      case RiskTypeEnum.strategic:
        headlineRiskId = risk.strategicRiskType;
        break;

      case RiskTypeEnum.operational:
        headlineRiskId = risk.operationalRiskType;
        break;

      case RiskTypeEnum.financial:
        headlineRiskId = risk.financialRiskType;
        break;
    }

    return this.headlineRisks$.pipe(
      map((headlineRisks) =>
        headlineRisks.find(headlineRisk =>
          headlineRisk.riskType === risk.riskType &&
          headlineRisk.id === headlineRiskId
        )
      ));
  }

  //gets risk type and risk headline data
  public getRiskConfig(): Observable<RiskConfig> {
    if (!this.riskCahceService.riskConfig$) {
      let riskCategories$ = this.initialiseRiskConfig();
      this.riskCahceService.setRiskConfig(riskCategories$);
    }
    return this.riskCahceService.riskConfig$;
  }

  //initialises risk cache with risk type and headline types data
  private initialiseRiskConfig(): Observable<RiskConfig> {
    return this.repository.getData('Risks/GetStaticRiskData').pipe(
      map(response => {
        let riskTypes = response["riskTypes"] as RiskType[];
        let strategicRiskTypes = response["strategicRiskTypes"] as HeadlineRisk[];
        let financialRiskTypes = response["financialRiskTypes"] as HeadlineRisk[];
        let operationalRiskTypes = response["operationalRiskTypes"] as HeadlineRisk[];

        strategicRiskTypes.forEach(headlineRisk => headlineRisk.riskType = RiskTypeEnum.strategic);
        operationalRiskTypes.forEach(headlineRisk => headlineRisk.riskType = RiskTypeEnum.operational);
        financialRiskTypes.forEach(headlineRisk => headlineRisk.riskType = RiskTypeEnum.financial);

        let headlineRisks = [
          ...strategicRiskTypes,
          ...financialRiskTypes,
          ...operationalRiskTypes
        ] as HeadlineRisk[];

        return new RiskConfig(riskTypes, headlineRisks);
      }),
      shareReplay()
    );
  }
  
  public getMyRisks() {
    return this.getRisksForCategory(RiskCategory.MyRisks);
  }

  public getMyManagerRisks() {
    return this.getRisksForCategory(RiskCategory.MyManagerRisks);
  }

  public getAllRisksWithAccessControl(user: RiskUser) {
    // Only allow get all Risks if user has access rights for strat and fin
    if (user.financialAccess && user.strategicAccess) {
      this.getAllRisks();
      return;
    }

  }

  public getAllRisks(): Observable<Risk[]> {
    return this.getRisksForCategory(RiskCategory.AllRisks);
  }

    // TODO: get from db
    riskProbabilities = [
      { min: 0, max: 10, label: 'Remote' },
      { min: 11, max: 30, label: 'Unlikely' },
      { min: 31, max: 60, label: 'Possible' },
      { min: 61, max: 80, label: 'Likely' },
      { min: 81, max: 100, label: 'Almost Certain' }
    ] as RiskProbability[];
  
    // TODO: get from db
    riskImpacts = [
      { min: 0, max: 10, label: 'Low' },
      { min: 11, max: 30, label: 'Moderate' },
      { min: 31, max: 60, label: 'Medium' },
      { min: 61, max: 100, label: 'High' }
    ] as RiskImpact[];
}

export interface RiskProbability {
  min: number;
  max: number;
  label: string;
}

export interface RiskImpact {
  min: number;
  max: number;
  label: string;
}

